import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MessageSquare, Phone, Video, CreditCard, ChevronLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { api, useAuth } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { formatCurrency } from '../utils/currency';

const TIME_SLOTS = [
  '09:00', '10:00', '11:00', '12:00', '14:00', '15:00', '16:00', '17:00', '18:00'
];

const CONSULTATION_MODES = [
  { id: 'chat', label: 'Chat', icon: MessageSquare, description: 'Text-based consultation via WhatsApp' },
  { id: 'call', label: 'Phone Call', icon: Phone, description: 'Voice call consultation' },
  { id: 'in-person', label: 'In-Person', icon: Video, description: 'Face-to-face meeting at lawyer\'s office' },
];

const BookingPage = () => {
  const { lawyerId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [lawyer, setLawyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedTime, setSelectedTime] = useState('');
  const [consultationMode, setConsultationMode] = useState('chat');
  const [caseDescription, setCaseDescription] = useState('');
  const [bookingId, setBookingId] = useState(null);

  const fetchLawyer = useCallback(async () => {
    try {
      const res = await api.get(`/lawyers/${lawyerId}`);
      setLawyer(res.data);
    } catch (err) {
      toast.error('Lawyer not found');
      navigate('/lawyers');
    } finally {
      setLoading(false);
    }
  }, [lawyerId, navigate]);

  useEffect(() => {
    fetchLawyer();
    // Generate next 7 days
    const dates = [];
    for (let i = 1; i <= 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    setSelectedDate(dates[0]);
  }, [fetchLawyer]);

  const getNextDays = () => {
    const days = [];
    for (let i = 1; i <= 7; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      days.push({
        value: date.toISOString().split('T')[0],
        label: date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      });
    }
    return days;
  };

  const handleBooking = async () => {
    if (!selectedDate || !selectedTime) {
      toast.error('Please select a date and time');
      return;
    }
    if (!caseDescription.trim()) {
      toast.error('Please describe your case');
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post('/bookings', {
        lawyer_id: lawyerId,
        date: selectedDate,
        time_slot: selectedTime,
        consultation_mode: consultationMode,
        case_description: caseDescription
      });
      setBookingId(res.data.booking_id);
      toast.success('Booking created! Proceed to payment.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Booking failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handlePayment = async () => {
    if (!bookingId) return;

    setPaymentLoading(true);
    try {
      // Create payment order
      const orderRes = await api.post('/payments/create-order', {
        booking_id: bookingId,
        amount: lawyer.consultation_fee
      });

      const { razorpay_order_id, amount, razorpay_key_id, payment_id } = orderRes.data;

      // Check if Razorpay is available (real integration)
      if (window.Razorpay && razorpay_key_id && !razorpay_key_id.includes('mock')) {
        const options = {
          key: razorpay_key_id,
          amount: amount,
          currency: 'INR',
          name: 'LegalHub',
          description: `Consultation with ${lawyer.user?.name}`,
          order_id: razorpay_order_id,
          handler: async (response) => {
            try {
              await api.post('/payments/verify', {
                payment_id: payment_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              });
              toast.success('Payment successful! Booking confirmed.');
              navigate('/dashboard');
            } catch (err) {
              toast.error('Payment verification failed');
            }
          },
          prefill: {
            name: user?.name,
            email: user?.email,
            contact: user?.mobile
          },
          theme: {
            color: '#0F172A'
          }
        };
        
        const rzp = new window.Razorpay(options);
        rzp.open();
      } else {
        // Mock payment for demo
        await api.post('/payments/verify', {
          payment_id: payment_id,
          razorpay_payment_id: `mock_pay_${Date.now()}`,
          razorpay_signature: 'mock_signature'
        });
        toast.success('Payment successful! Booking confirmed. (Demo Mode)');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Payment failed');
    } finally {
      setPaymentLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="container-app py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-1/4 mb-8" />
            <div className="bg-white rounded-2xl p-8 h-96" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="container-app py-8">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-5 h-5" />
          Back
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Booking Form */}
          <div className="lg:col-span-2">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl p-8 border border-slate-200"
            >
              <h1 className="text-2xl font-bold text-slate-900 mb-6">Book Consultation</h1>

              {!bookingId ? (
                <div className="space-y-8">
                  {/* Date Selection */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-amber-600" />
                      Select Date
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-7 gap-2">
                      {getNextDays().map(day => (
                        <button
                          key={day.value}
                          onClick={() => setSelectedDate(day.value)}
                          className={`p-3 rounded-xl text-center transition-colors ${
                            selectedDate === day.value
                              ? 'bg-amber-600 text-white'
                              : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                          }`}
                          data-testid={`date-${day.value}`}
                        >
                          <span className="text-xs block">{day.label.split(' ')[0]}</span>
                          <span className="font-semibold">{day.label.split(' ')[2]}</span>
                          <span className="text-xs block">{day.label.split(' ')[1]}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Time Selection */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Clock className="w-5 h-5 text-amber-600" />
                      Select Time
                    </h3>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      {TIME_SLOTS.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          className={`p-3 rounded-xl text-center transition-colors ${
                            selectedTime === slot
                              ? 'bg-slate-900 text-white'
                              : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                          }`}
                          data-testid={`time-${slot}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Consultation Mode */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Consultation Mode</h3>
                    <RadioGroup value={consultationMode} onValueChange={setConsultationMode}>
                      <div className="grid md:grid-cols-3 gap-4">
                        {CONSULTATION_MODES.map(mode => (
                          <div key={mode.id} className="relative">
                            <RadioGroupItem
                              value={mode.id}
                              id={mode.id}
                              className="peer sr-only"
                              data-testid={`mode-${mode.id}`}
                            />
                            <Label
                              htmlFor={mode.id}
                              className="flex flex-col items-center p-4 bg-slate-50 rounded-xl cursor-pointer peer-data-[state=checked]:bg-amber-50 peer-data-[state=checked]:border-2 peer-data-[state=checked]:border-amber-600 hover:bg-slate-100 transition-colors"
                            >
                              <mode.icon className="w-8 h-8 text-slate-600 peer-data-[state=checked]:text-amber-600" />
                              <span className="font-medium mt-2">{mode.label}</span>
                              <span className="text-xs text-slate-500 text-center mt-1">{mode.description}</span>
                            </Label>
                          </div>
                        ))}
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Case Description */}
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-4">Describe Your Case</h3>
                    <Textarea
                      value={caseDescription}
                      onChange={(e) => setCaseDescription(e.target.value)}
                      placeholder="Briefly describe your legal matter..."
                      rows={4}
                      data-testid="case-description-input"
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      This helps the lawyer prepare for your consultation
                    </p>
                  </div>

                  <Button
                    onClick={handleBooking}
                    disabled={submitting}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-full py-6"
                    data-testid="create-booking-btn"
                  >
                    {submitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating Booking...
                      </span>
                    ) : (
                      'Proceed to Payment'
                    )}
                  </Button>
                </div>
              ) : (
                /* Payment Section */
                <div className="space-y-6">
                  <div className="bg-emerald-50 p-4 rounded-xl border border-emerald-200">
                    <p className="text-emerald-800 font-medium">Booking Created Successfully!</p>
                    <p className="text-sm text-emerald-600 mt-1">
                      Complete payment to confirm your consultation
                    </p>
                  </div>

                  <div className="bg-slate-50 p-6 rounded-xl">
                    <h3 className="font-semibold text-slate-900 mb-4">Booking Summary</h3>
                    <div className="space-y-3 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Lawyer</span>
                        <span className="font-medium">{lawyer.user?.name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Date</span>
                        <span className="font-medium">{selectedDate}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Time</span>
                        <span className="font-medium">{selectedTime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Mode</span>
                        <span className="font-medium capitalize">{consultationMode}</span>
                      </div>
                      <div className="pt-3 border-t border-slate-200 flex justify-between">
                        <span className="font-medium">Total Amount</span>
                        <span className="text-xl font-bold text-slate-900">{formatCurrency(lawyer.consultation_fee)}</span>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handlePayment}
                    disabled={paymentLoading}
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6"
                    data-testid="pay-now-btn"
                  >
                    {paymentLoading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Pay {formatCurrency(lawyer.consultation_fee)}
                      </span>
                    )}
                  </Button>
                </div>
              )}
            </motion.div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-2xl p-6 border border-slate-200 sticky top-24"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center text-2xl font-bold text-slate-600">
                  {lawyer.user?.name?.charAt(0)}
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{lawyer.user?.name}</h3>
                  <p className="text-sm text-slate-500">{lawyer.qualification}</p>
                </div>
              </div>
              
              <div className="py-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">Consultation Fee</p>
                <p className="text-2xl font-bold text-slate-900">{formatCurrency(lawyer.consultation_fee)}</p>
              </div>

              {/* Policies */}
              <div className="mt-4 space-y-3">
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-800">
                    <strong>Cancellation Policy:</strong> Free cancellation up to 24 hours before the appointment.
                  </p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-600">
                    LegalHub is not a law firm. We connect clients with independent lawyers. Outcomes are not guaranteed.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingPage;
